"use client";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/hongkong-setup-v2/ui/badge";
import { Card, CardContent } from "@/components/hongkong-setup-v2/ui/card";
import gsap from "gsap";

import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import Script from "next/script";

import { testimonials, videoTestimonials } from "@/components/hongkong-setup-v2/testimonials";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import TestimonialCard from "@/components/hongkong-setup-v2/ui/TestimonialCard";
import { useSwiperEarlyDestroy } from "@/hooks/use-swiper-early-destroy";

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

// Undocumented but long-stable workaround: cc_load_policy only skips forcing captions on,
// it doesn't turn them off. Actually disabling them needs live JS API calls, reapplied on
// every PLAYING state since a loop restart can silently re-enable the captions module.
const disableCaptions = (player: any) => {
  try {
    player.setOption("captions", "track", {});
    player.unloadModule("captions");
  } catch (err) {
    // unofficial API surface — safe to ignore if a call isn't supported yet
  }
};

export const GeneralSection = () => {
  const { onSwiper } = useSwiperEarlyDestroy();
  const row1Ref = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const swiperSectionRef = useRef<HTMLDivElement>(null);

  // Videos (YouTube Shorts, controlled via the IFrame Player API)
  const playerContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const playersRef = useRef<any[]>([]);
  const [mutedStates, setMutedStates] = useState(
    videoTestimonials.map((vid) => vid.muted)
  );

  useEffect(() => {
    const createPlayers = () => {
      videoTestimonials.forEach((vid, index) => {
        const container = playerContainerRefs.current[index];
        if (!container || playersRef.current[index]) return;

        // The IFrame API doesn't render *into* its target element — it
        // replaces it outright with an <iframe>. Handing it `container`
        // itself (the div React's ref is attached to) would leave React's
        // fiber tree pointing at a node that's no longer in the document,
        // so a later unmount's `removeChild` on it throws "The node to be
        // removed is not a child of this node." A plain child div, created
        // and owned entirely outside JSX, absorbs that replacement instead —
        // `container` itself is never touched, so React's bookkeeping for it
        // stays valid for as long as the component is mounted.
        const mount = document.createElement("div");
        mount.className = "h-full w-full";
        container.appendChild(mount);

        playersRef.current[index] = new window.YT.Player(mount, {
          width: "100%",
          height: "100%",
          videoId: vid.youtubeId,
          playerVars: {
            autoplay: 1,
            mute: vid.muted ? 1 : 0,
            loop: 1,
            playlist: vid.youtubeId,
            controls: 0,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
            enablejsapi: 1,
          },
          events: {
            onReady: (e: any) => disableCaptions(e.target),
            onStateChange: (e: any) => {
              if (e.data === window.YT.PlayerState.PLAYING) {
                disableCaptions(e.target);
              }
            },
          },
        });
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayers();
    } else {
      const previousCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previousCallback?.();
        createPlayers();
      };
    }

    return () => {
      playersRef.current.forEach((player) => player?.destroy?.());
      playersRef.current = [];
    };
  }, []);

  // Swipe hint GIF visibility (mobile only)
  const [showSwipeHint, setShowSwipeHint] = useState(false);

  const toggleMute = (index: number) => {
    const currentlyMuted = mutedStates[index];

    // Create new states: unmute clicked video, mute all others
    const newMutedStates = mutedStates.map((_, i) => {
      if (i === index) {
        return !currentlyMuted; // Toggle the clicked video
      } else {
        return true; // Mute all other videos
      }
    });

    setMutedStates(newMutedStates);

    // Update all YouTube players directly (no iframe reload)
    playersRef.current.forEach((player, i) => {
      if (!player) return;
      if (newMutedStates[i]) player.mute();
      else player.unMute();
    });
  };

  // Show swipe hint 3 seconds after entering swiper section, hide after 5 more seconds
  useEffect(() => {
    const swiperSection = swiperSectionRef.current;
    if (!swiperSection) return;

    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Wait 3 seconds before showing the hint
            showTimer = setTimeout(() => {
              setShowSwipeHint(true);

              // Hide after 5 more seconds
              hideTimer = setTimeout(() => {
                setShowSwipeHint(false);
              }, 5000);
            }, 3000);

            // Stop observing after first trigger
            observer.unobserve(swiperSection);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
      }
    );

    observer.observe(swiperSection);

    return () => {
      observer.disconnect();
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  // ========== GSAP SCROLLER ==========
  useEffect(() => {
    const row = row1Ref.current;
    if (!row) return;

    let totalWidth = row.scrollWidth / 2;

    gsap.set(row, { x: 0 });

    const tl = gsap.to(row, {
      x: -totalWidth,
      duration: 40,
      ease: "none",
      repeat: -1,
      modifiers: {
        x: gsap.utils.unitize((value) => {
          const v = parseFloat(value);
          return v <= -totalWidth ? v + totalWidth : v;
        }),
      },
    });

    const wrapper = wrapperRef.current;
    const slowDown = () => tl.timeScale(0.2);
    const speedUp = () => tl.timeScale(1);

    wrapper?.addEventListener("mouseenter", slowDown);
    wrapper?.addEventListener("mouseleave", speedUp);

    return () => {
      wrapper?.removeEventListener("mouseenter", slowDown);
      wrapper?.removeEventListener("mouseleave", speedUp);
      tl.kill();
    };
  }, []);

  return (
    <section className="flex flex-col w-full items-center gap-[50px] py-[70px] px-4 sm:px-0 relative">
      <Script src="https://www.youtube.com/iframe_api" strategy="afterInteractive" />

      {/* ====================== HEADING ====================== */}
      <div className="relative w-full max-w-[1440px] text-center">
        <Badge className="bg-[#0a0f1e] mb-4 mx-auto flex justify-center w-fit rounded-[32px] border border-solid border-[#14224d] shadow-[inset_0px_-7px_11px_#6fa6ff1f] backdrop-blur-[2px] px-[15px] py-[5px]">
          <span className="font-medium text-[#ffff] text-sm">
            Wall of love
          </span>
        </Badge>

        <h2 className="font-['SharpGroteskPeTrialBook-20',sans-serif] font-normal text-[36px] leading-[34px]  sm:text-[70px] sm:leading-[48px] md:leading-[58px] lg:leading-[120%] text-transparent bg-clip-text bg-[radial-gradient(50%_50%_at_44%_21%,rgba(21,93,252,1)_0%,rgba(255,255,255,1)_100%)]">
          <span className="bg-[radial-gradient(38.63%_62.02%_at_43.72%_70.95%,#155dfc_10%,#FFFFFF_100%)] bg-clip-text text-transparent">Our Clients Come</span>
          <br />
          First, {" "}
          
         
              <span className="font-['SharpGroteskPeTrialBook-20',sans-serif] font-normal">
                Always
              </span> 
        </h2>

        <p className="text-[#e2edffb2] font-[300] text-[14px] sm:text-[16px] max-w-[600px] mx-auto">
          We take pride in the moment our clients become our friends.
        </p>
      </div>

      {/* ====================== VIDEO SWIPER ====================== */}
      <div
        ref={swiperSectionRef}
        className="relative w-full max-w-[1216px] px-0 lg:px-2"
      >

        {showSwipeHint && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] sm:hidden pointer-events-none">
            <Image
              src="/hongkong-setup-v2/videos/swipeleftgif.gif"
              alt="Swipe left hint"
              className="w-24 h-24 drop-shadow-2xl"
              width={96}
              height={96}
              unoptimized
            />
          </div>
        )}

       
        <Swiper
          onSwiper={onSwiper}
          modules={[Navigation]}
          navigation={{
            nextEl: ".nextBtn",
            prevEl: ".prevBtn",
          }}
          spaceBetween={20}
          slidesPerView={1}
          loop={true}
          breakpoints={{
            640: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          className="w-full h-[500px]"
        >
          {videoTestimonials.map((vid, index) => (
            <SwiperSlide key={index}>
              <Card className="relative w-full h-[500px] bg-[#171717] rounded-[20px] border-[15px] border-[#171717] overflow-hidden">
                <CardContent className="relative w-full h-full p-0 rounded-[20px] overflow-hidden">

                  {/* Crop wrapper: keeps the 9:16 video at its native aspect ratio while
                      oversizing it so it fills the card edge-to-edge instead of letterboxing */}
                  <div className="absolute left-1/2 top-1/2 w-full min-h-full aspect-[9/16] -translate-x-1/2 -translate-y-1/2">
                    <div
                      ref={(el) => {
                        playerContainerRefs.current[index] = el;
                      }}
                      className="w-full h-full pointer-events-none"
                    />
                  </div>


                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 rounded-[20px] to-transparent pointer-events-none" />


                  <button
                    onClick={() => toggleMute(index)}
                    className="absolute z-20 flex items-center justify-center w-10 h-10 text-white rounded-full top-4 right-4 bg-black/50 border border-white/30 backdrop-blur-md"
                  >
                    <Image
                      src={mutedStates[index] ? "/hongkong-setup-v2/mute.png" : "/hongkong-setup-v2/unmute.png"}
                      className="w-5 h-5 invert"
                      alt={mutedStates[index] ? "Muted" : "Unmuted"}
                      width={20}
                      height={20}
                    />
                  </button>
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>

        
        <button className="prevBtn hidden sm:flex absolute left-[-50px] top-1/2 -translate-y-1/2 z-[60] bg-white/15 hover:bg-white/25 backdrop-blur-md text-white  rounded-full items-center justify-center">
          <Image
            src="/hongkong-setup-v2/left-arrow.png"
            alt="Previous"
            className="w-10 h-10 object-contain"
            width={60}
            height={60}
          />
        </button>

        <button className="nextBtn hidden sm:flex absolute right-[-50px] top-1/2 -translate-y-1/2 z-[60] bg-white/15 hover:bg-white/25 backdrop-blur-md text-white rounded-full items-center justify-center">
          <Image
            src="/hongkong-setup-v2/right-arrow.png"
            alt="Next"
            className="w-10 h-10 object-contain"
            width={60}
            height={60}
          />
        </button>
      </div>

      {/* ====================== BOTTOM SCROLLER ====================== */}
      <div
        ref={wrapperRef}
        className="relative w-full max-w-[1216px] h-[330px] lg:h-[285px] overflow-hidden"
      >
        <div
          ref={row1Ref}
          className="absolute top-0 left-0 flex flex-row items-center gap-6 w-max"
        >
          {testimonials.map((t, i) => (
            <TestimonialCard key={`t1-${i}`} {...t} />
          ))}

          {testimonials.map((t, i) => (
            <TestimonialCard key={`t2-${i}`} {...t} />
          ))}
        </div>

        {/* Gradient Masks */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-black/80 via-transparent to-black/80" />
      </div>
    </section>
  );
};
